import { IScope } from "angular";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../../../../../records/forms/formView";
import { StateService } from "@uirouter/angularjs";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../../../records/forms/formly/fieldConfigurationHelpers";
import {
  RecordsUtilsService,
  vsName as RecordsUtilsServiceName,
} from "../../../../../../records/utils";
import { RecordFileTemplateOptions } from "../../../../../../records/forms/formly/types";
import fieldHelpers from "../../../../../../ui-formly/fieldHelpers";
import { MachineConfigurationView } from "../../../../models/machineConfiguration";
import { cloneDeep, get } from "lodash";
import { IExpressionFunction, IFieldArray } from "AngularFormly";
import * as lookupNames from "../../../../configs/lookup";
import { LookupManagementService } from "../../../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as MachineLookupManagementServiceName } from "../../../../../shared/lookups";
import { Property } from "../../../../../../property/model/Property";
import { Record } from "../../../../../../records/server/crud";
import { MachineConfigurationTypeView } from "../../../../models/machineConfigurationType";

interface IMultipleMoveFormModel {
  MachineChanges: IMachineMoveRequest[];
  Date?: Date;
  Files: File[];
  Notes?: string;
}

interface IMachineMoveRequest {
  MachineId: number;
  SlotNumber?: string;
  Section?: string;
  Bank?: string;
  Position?: string;
  PropertyId?: number;
  Property?: Property;
  BankConfigId?: number;
  BankConfig?: Record;
  MachineConfigurationTypeId?: number;
  MachineConfigurationType?: Record;
}

export class MachinesMoveViewComponentController {
  /* bindings */
  machineConfigurations!: MachineConfigurationView[];
  /* end bindings */

  model!: IMultipleMoveFormModel;
  machineInfo!: {
    machineConfigurations: MachineConfigurationView[];
  };
  formFields!: IFieldArray;

  static $inject = [
    FormViewServiceName,
    "$state",
    FieldConfigurationHelperServiceName,
    "$scope",
    RecordsUtilsServiceName,
    MachineLookupManagementServiceName,
  ];
  constructor(
    private readonly formViewService: FormViewService,
    private readonly $state: StateService,
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    $scope: IScope,
    private readonly recordUtils: RecordsUtilsService,
    private readonly machineLookupManagementService: LookupManagementService
  ) {
    $scope.$on("copyDown", (e, modelClicked) => {
      e.preventDefault();
      this.copyDown(modelClicked);
    });
  }

  private initializeFormFields() {
    const fileTemplateOptions: RecordFileTemplateOptions = {
      allowMultiple: true,
      label: "Files",
    };

    this.formFields = [
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleDateField({
            key: "Date",
            name: "Date",
            label: "Change Date",
            required: true,
          }),
        ],
        4
      ),
      this.fieldConfigurationHelperService.repeaterField({
        key: "MachineChanges",
        name: "MachineChanges",
        allowAdd: false,
        allowAddBottom: false,
        displayNameSingular: "Machine",
        repeaterFields: fieldHelpers.columnLayout(
          [
            fieldHelpers.simpleReadonlyField({
              key: "MachineId",
              className: "col-xs-3",
              label: "Serial Number",
              formatters: [
                (machineId) => {
                  const machineConfigurationView =
                    this.getMachineConfigurationFromMachineId(
                      <number>machineId
                    );
                  return machineConfigurationView?.SerialNumber;
                },
              ],
            }),
            fieldHelpers.simpleStringField({
              key: "SlotNumber",
              className: "col-xs-3",
              label: "Machine Number",
              required: true,
              expressionProperties: {
                "templateOptions.helpText": this.previousText(),
              },
            }),
            this.fieldConfigurationHelperService.selectAsyncField({
              key: "MachineConfigurationTypeId",
              label: "Configuration Type",
              required: true,
              queryUrl: "/api/machines/MachineConfigurationType",
              serverItemMapper: (model: MachineConfigurationTypeView) => {
                return {
                  Id: model.Id,
                  Name: model.MachineConfigurationTypeName!,
                };
              },
              onSelect(selectedItem: unknown, model: unknown) {
                (<any>model).MachineConfigurationType = selectedItem;
              },
              getDefaultItem: (model: any) =>
                get(model, "MachineConfigurationType"),
            }),
            this.fieldConfigurationHelperService.selectAsyncField({
              queryUrl: "/api/properties",
              getDefaultItem: (model: any) => model.Property,
              label: "Property",
              key: "PropertyId",
              required: true,
              onSelect(selectedItem: unknown, model: unknown) {
                (<any>model).Property = selectedItem;
              },
              expressionProperties: {
                "templateOptions.helpText": this.previousText(
                  (v) => v?.PropertyName
                ),
              },
            }),
            fieldHelpers.simpleStringField({
              key: "Section",
              label: "Section",
              required: true,
              expressionProperties: {
                "templateOptions.helpText": this.previousText(),
              },
            }),
            fieldHelpers.simpleStringField({
              key: "Bank",
              label: "Bank",
              required: true,
              expressionProperties: {
                "templateOptions.helpText": this.previousText(),
              },
            }),
            fieldHelpers.simpleStringField({
              key: "Position",
              label: "Position",
              required: true,
              expressionProperties: {
                "templateOptions.helpText": this.previousText(),
              },
            }),
            this.fieldConfigurationHelperService.lookupField(
              this.machineLookupManagementService,
              {
                key: "BankConfigId",
                label: "Bank Config",
                required: true,
                objectName: lookupNames.bankConfigName,
                expressionProperties: {
                  "templateOptions.helpText": this.previousText(
                    (v) => v?.BankConfigName
                  ),
                },
              }
            ),
            {
              key: "copyDown",
              name: "copyDown",
              className: "col-xs-3",
              templateOptions: {
                label: " ",
              },
              template: `
<div>
  <div class="pull-right">
    <div class="btn-group">
      <div class="btn btn-primary game-config-btn" ng-click="$emit('copyDown', model)">
        <i class="glyphicon glyphicon-arrow-down"></i>
      </div>
    </div>
  </div>
</div>
        `,
              wrapper: ["bootstrapLabel", "bootstrapHasError"],
            },
          ],
          4
        ),
        showHeader: true,
      }),
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: fileTemplateOptions,
      },
      fieldHelpers.headerField({ headerText: "Notes", key: "NotesHeader" }),
      fieldHelpers.textAreaField({
        key: "Notes",
        name: "Notes",
        label: " ",
        rows: 5,
      }),
    ];
  }

  $onInit(): void {
    this.model = {
      Files: [],
      MachineChanges: (this.machineConfigurations ?? []).map((mc) => {
        return {
          MachineId: mc.MachineId,
          SlotNumber: <string | undefined>mc.SlotNumber,
          Section: undefined,
          Bank: undefined,
          Slot: undefined,
          PropertyId: mc.PropertyId,
          Property: { Name: <string>mc.PropertyName, Id: mc.PropertyId! },
          BankConfigId: mc.BankConfigId,
          BankConfig: { Name: mc.BankConfigName, Id: mc.BankConfigId! },
          MachineConfigurationTypeId: mc.MachineConfigurationTypeId,
          MachineConfigurationType: {
            Id: mc.MachineConfigurationTypeId!,
            Name: mc.MachineConfigurationTypeName,
          },
        };
      }),
    };
    this.machineInfo = {
      machineConfigurations: cloneDeep(this.machineConfigurations),
    };
    this.initializeFormFields();
  }

  copyDown(modelClicked: IMachineMoveRequest) {
    const machineMoves = this.model.MachineChanges;
    const copyIndex = machineMoves.indexOf(modelClicked);
    for (let i = copyIndex + 1; i < machineMoves.length; i++) {
      const newModel: IMachineMoveRequest = {
        MachineId: machineMoves[i].MachineId,
        SlotNumber: machineMoves[i].SlotNumber,
        PropertyId: modelClicked.PropertyId,
        Property: modelClicked.Property,
        Section: modelClicked.Section,
        Bank: modelClicked.Bank,
        BankConfigId: modelClicked.BankConfigId,
        BankConfig: modelClicked.BankConfig,
        MachineConfigurationTypeId: machineMoves[i].MachineConfigurationTypeId,
        MachineConfigurationType: machineMoves[i].MachineConfigurationType,
      };
      this.recordUtils.assignId(newModel);
      machineMoves.splice(i, 1, newModel);
    }
  }

  private previousText(
    fieldSelector?: (
      machineConfigurationView: MachineConfigurationView | undefined
    ) => any
  ): IExpressionFunction {
    return (_, __, scope) => {
      const machineConfiguration = this.getMachineConfigurationFromMachineId(
        scope.model?.MachineId
      );

      const fieldKey = scope.options.key;
      fieldSelector ??= (view) => (view ? view[fieldKey] : undefined);

      const value = fieldSelector(machineConfiguration);
      return value ? `Previous: ${value}` : undefined;
    };
  }

  async submit(formViewController: FormViewController): Promise<void> {
    await this.formViewService.submitForm({
      url: `/api/machines/move`,
      formObject: this.model,
      formViewController,
      method: "post",
    });

    await this.$state.go("^.grid", undefined, { reload: true });
  }

  async cancel(): Promise<void> {
    await this.$state.go("^.grid", undefined, { reload: true });
  }

  private getMachineConfigurationFromMachineId(machineId: number) {
    return this.machineInfo.machineConfigurations.find(
      (mc) => mc.MachineId === machineId
    );
  }
}
